import HOMEPAGE_SEARCH_INDEX_CONFIG from '@/app/config/homepage'
import { ROUTES } from '@/app/lib/constants'
import { loadLocaleFiles } from '@/app/lib/load-locale'
import { CoursesPage } from '@/app/modules/course/pages'
import { ExternalCoursesPage } from '@/app/modules/course/pages/ExternalCoursesPage'
import { generateTypesenseScopedKey } from '@/app/modules/search/lib/keys'
import { getSearchClient } from '@/app/modules/search/lib/search-client'
import {
  buildInitialResults,
  sanitizeForSerialization,
} from '@/app/modules/search/lib/ssr'
import { AvailableLanguages } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import type {
  ICategoryWithSubcategoriesHit,
  IHomePageSubCategoryWithCoursesHit,
} from '@/types/search'
import { isBoom } from '@hapi/boom'
import { InitialResults } from 'instantsearch.js'

type ScopedApiKeys = {
  subcategories_courses: string
  categories: string
}

type SearchHit =
  | IHomePageSubCategoryWithCoursesHit<AvailableLanguages>
  | ICategoryWithSubcategoriesHit

const ITEMS_PER_GROUP = 3
const MAX_GROUPS = 6

// This is the API key used to generate scoped search keys - it should NOT be exposed to the client
const frontendSearchApiKey = process.env.NEXT_SEARCH_INDEX_FRONTEND_API_KEY

export async function getServerSideProps(context) {
  const { locale } = context

  try {
    const messages = await loadLocaleFiles(context.locale, [
      'strings',
      'search-page',
      'navigation',
    ])

    if (HOMEPAGE_SEARCH_INDEX_CONFIG.enabled) {
      if (!frontendSearchApiKey) {
        throw new Error('NEXT_SEARCH_INDEX_FRONTEND_API_KEY is not configured')
      }

      const scopedApiKeys: ScopedApiKeys = {
        // Filter to only show things that have published courses
        subcategories_courses: generateTypesenseScopedKey(
          frontendSearchApiKey,
          '{"filter_by":"$courses(published:=true)"}'
        ),
        // Filter to only show active categories that have published courses
        categories: generateTypesenseScopedKey(
          frontendSearchApiKey,
          JSON.stringify({
            filter_by:
              '$subcategories(is_active:=true&&$subcategories_courses($courses(published:=true)))',
            exclude_fields:
              '$subcategories($subcategories_courses(course_id,subcategory_id,$courses(*)))',
          })
        ),
      }

      const searchClient = getSearchClient('', {
        collectionSpecificSearchParameters: {
          subcategories_courses: {
            q: '*',
            group_by: 'subcategory_id',
            group_limit: ITEMS_PER_GROUP,
            include_fields: `$subcategories(name, id, is_promoted, slug) as subcategory,$courses(id,course_id,name_${locale},learning_description_${locale},quality_score,education_provider,instructors,level,min_price) as course`,
            exclude_fields: 'id,course_id,subcategory_id',
            sort_by:
              '$subcategories(display_order:asc),$courses(quality_score:desc,_rand():desc)',
            limit: MAX_GROUPS,
            query_by: `subcategory_id`,
            'x-typesense-api-key': scopedApiKeys.subcategories_courses,
          },
          categories: {
            sortBy: 'display_order:desc,name:asc',
            query_by: `name`,
            limit: 10,
            include_fields: `id,name,slug,$subcategories(id,name,slug)`,
            q: '*',
            'x-typesense-api-key': scopedApiKeys.categories,
          },
        },
      })

      const searchRequests = [
        { indexName: 'subcategories_courses', params: { query: '' } },
        { indexName: 'categories', params: { query: '' } },
      ]

      const { results } = await searchClient.search<SearchHit>(searchRequests)

      if (!results || results.length !== 2) {
        return {
          notFound: true,
          props: {
            messages,
            locale,
          },
        }
      }

      const initialResults = [
        buildInitialResults(results[0], 'subcategories_courses'),
        buildInitialResults(results[1], 'categories'),
      ]

      return {
        props: {
          // Sanitize to remove undefined values that can't be serialized by Next.js
          initialResults: sanitizeForSerialization(initialResults),
          messages,
          scopedApiKeys,
        },
      }
    }

    return {
      props: {
        messages,
      },
    }
  } catch (e) {
    console.error(e)
    if (isBoom(e, 401)) {
      return {
        redirect: {
          destination: ROUTES.logout,
          permanent: false,
          locale,
        },
      }
    } else if (isBoom(e, 403)) {
      return {
        redirect: {
          destination: `/${locale}${ROUTES.accessDenied}`,
          permanent: false,
          locale,
        },
      }
    }

    throw e
  }
}

type HomePageProps = {
  scopedApiKeys?: ScopedApiKeys
  initialResults?: InitialResults[]
}

export default function HomePage({
  scopedApiKeys,
  initialResults,
  ...props
}: HomePageProps) {
  if (HOMEPAGE_SEARCH_INDEX_CONFIG.enabled) {
    if (!scopedApiKeys) {
      throw new Error('scopedApiKeys is required when search is enabled')
    }
    return (
      <ExternalCoursesPage
        {...props}
        scopedApiKeys={scopedApiKeys}
        initialResults={initialResults}
      />
    )
  }

  return <CoursesPage />
}
